import { Alert, Badge, Button, Card, Checkbox, Group, Loader, Select, Stack, Table, Text, TextInput, Textarea, Title } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import {
  IExternalIssueBanner,
  TExternalIssueBannerSeverity,
  useCreateExternalIssueBannerMutation,
  useDeleteExternalIssueBannerMutation,
  useGetExternalIssueBannersQuery,
  useUpdateExternalIssueBannerMutation,
} from "../../externalIssueBannerRTK";

const severityOptions: { value: TExternalIssueBannerSeverity; label: string }[] = [
  { value: "error", label: "Feil" },
  { value: "warning", label: "Advarsel" },
  { value: "info", label: "Info" },
];

const severityColor = (severity: TExternalIssueBannerSeverity) => {
  if (severity === "warning") return "yellow";
  if (severity === "info") return "blue";
  return "red";
};

export const ExternalIssueBannersAdmin = () => {
  const { data: banners = [], isLoading, isError } = useGetExternalIssueBannersQuery();
  const [createBanner, { isLoading: isCreating }] = useCreateExternalIssueBannerMutation();
  const [source, setSource] = useState("");
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<TExternalIssueBannerSeverity>("error");
  const [isActive, setIsActive] = useState(true);

  const canCreate = source.trim().length > 0 && message.trim().length > 0;

  const addBanner = async () => {
    if (canCreate === false) return;

    await createBanner({
      source: source.trim(),
      message: message.trim(),
      severity,
      isActive,
    }).unwrap();

    setSource("");
    setMessage("");
    setSeverity("error");
    setIsActive(true);
  };

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={3}>Globale systemmeldinger</Title>
          {isLoading && <Loader size="sm" />}
        </Group>

        <Text size="sm" c="dimmed">
          Aktive meldinger vises som banner for alle innloggede brukere.
        </Text>

        {isError && (
          <Alert color="red" variant="light">
            Kunne ikke hente systemmeldinger.
          </Alert>
        )}

        <Group align="flex-end">
          <TextInput
            label="Kilde"
            placeholder="Azure"
            value={source}
            onChange={(event) => setSource(event.currentTarget.value)}
            style={{ flex: "1 1 12rem" }}
          />
          <Textarea
            label="Melding"
            placeholder="Azure er nede"
            autosize
            minRows={1}
            maxRows={3}
            value={message}
            onChange={(event) => setMessage(event.currentTarget.value)}
            style={{ flex: "2 1 24rem" }}
          />
          <Select
            label="Nivaa"
            data={severityOptions}
            value={severity}
            onChange={(value) => setSeverity((value ?? "error") as TExternalIssueBannerSeverity)}
            w="10rem"
            allowDeselect={false}
          />
          <Checkbox
            label="Vis banner"
            checked={isActive}
            onChange={(event) => setIsActive(event.currentTarget.checked)}
            pb={8}
          />
          <Button loading={isCreating} disabled={canCreate === false} onClick={addBanner}>
            Legg til
          </Button>
        </Group>

        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Kilde</Table.Th>
              <Table.Th>Melding</Table.Th>
              <Table.Th>Nivaa</Table.Th>
              <Table.Th>Handlinger</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {banners.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text size="sm" c="dimmed">
                    Ingen systemmeldinger er registrert.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {banners.map((banner) => (
              <ExternalIssueBannerRow key={banner._id} banner={banner} />
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
};

const ExternalIssueBannerRow = ({ banner }: { banner: IExternalIssueBanner }) => {
  const [updateBanner, { isLoading: isUpdating }] = useUpdateExternalIssueBannerMutation();
  const [deleteBanner, { isLoading: isDeleting }] = useDeleteExternalIssueBannerMutation();
  const [source, setSource] = useState(banner.source);
  const [message, setMessage] = useState(banner.message);
  const [severity, setSeverity] = useState<TExternalIssueBannerSeverity>(banner.severity);
  const [isActive, setIsActive] = useState(banner.isActive);

  useEffect(() => {
    setSource(banner.source);
    setMessage(banner.message);
    setSeverity(banner.severity);
    setIsActive(banner.isActive);
  }, [banner]);

  const patch = useMemo(
    () => ({
      source: source.trim(),
      message: message.trim(),
      severity,
      isActive,
    }),
    [isActive, message, severity, source],
  );

  const isDirty =
    patch.source !== banner.source ||
    patch.message !== banner.message ||
    patch.severity !== banner.severity ||
    patch.isActive !== banner.isActive;

  const canSave = isDirty && patch.source.length > 0 && patch.message.length > 0;

  const save = () => {
    if (canSave === false) return;
    updateBanner({ bannerId: banner._id, patch });
  };

  const remove = () => {
    deleteBanner({ bannerId: banner._id });
  };

  return (
    <Table.Tr>
      <Table.Td>
        <Checkbox checked={isActive} onChange={(event) => setIsActive(event.currentTarget.checked)} />
      </Table.Td>
      <Table.Td>
        <TextInput value={source} onChange={(event) => setSource(event.currentTarget.value)} />
      </Table.Td>
      <Table.Td>
        <Textarea
          autosize
          minRows={1}
          maxRows={3}
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <Select
          data={severityOptions}
          value={severity}
          onChange={(value) => setSeverity((value ?? "error") as TExternalIssueBannerSeverity)}
          allowDeselect={false}
        />
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Badge color={severityColor(severity)} variant="light">
            {isActive ? "Aktiv" : "Skjult"}
          </Badge>
          <Button size="xs" loading={isUpdating} disabled={canSave === false} onClick={save}>
            Lagre
          </Button>
          <Button size="xs" variant="subtle" color="red" loading={isDeleting} onClick={remove}>
            Slett
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};
